// components/views/RegularPaymentView.tsx
import { useEffect, useState, useRef } from "react";
import { PaymentMethodCard } from "@/components/private/PaymentMethodCard";
import { PaymentSummary } from "@/components/private/PaymentSummary";
import { axiosClient } from "@/lib/axiosClient";
import toast from "react-hot-toast";
import { useQueryClient } from "@tanstack/react-query";
import {
    CreditCard,
    Loader2,
    Zap,
    ArrowRight,
    AlertTriangle,
    Info // Ditambahkan untuk ikon di dalam kotak peringatan
} from "lucide-react";
import type { RegistrationItem } from "@/types/registration";
import type { PaymentData } from "@/types/payment";
import type { UserData } from "@/types/pic";
import StepNavigation from "../StepNavigation";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { AxiosError } from "axios";

interface RegularPaymentViewProps {
    user: UserData;
    activePeriodId: number;
    onNext: () => void;
    onBack: () => void;
    backDisabled?: boolean;
}

interface LaravelErrorResponse {
    message?: string;
}

export const RegularPaymentView = ({ user, activePeriodId, onNext, onBack, backDisabled = false }: RegularPaymentViewProps) => {
    const queryClient = useQueryClient();
    const [selectedMethod, setSelectedMethod] = useState<string>("8");
    const [registration, setRegistration] = useState<RegistrationItem | null>(null);
    const [payment, setPayment] = useState<PaymentData | null>(null);
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // STATE BARU: Untuk kontrol modal konfirmasi
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const promiseRef = useRef<{ resolve: () => void; reject: (reason?: unknown) => void } | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const [regRes, payRes] = await Promise.all([
                    axiosClient.get(`/participants/${user.participant_id}/registrations/period/${activePeriodId}`),
                    axiosClient.get(`/participants/${user.participant_id}/registrations/period/${activePeriodId}/payment`)
                ]);

                setRegistration(regRes.data.data);
                if (payRes.data.data) {
                    setPayment(payRes.data.data);
                    setSelectedMethod(payRes.data.data.gateway_transaction_id);
                }
            } catch (error: unknown) {
                console.error("Fetch Error:", error);
            } finally { setLoading(false); }
        };
        fetchData();
    }, [user.participant_id, activePeriodId]);

    // 1. Eksekusi API utama (dipanggil jika konfirmasi "Ya")
    const executePayment = async (): Promise<void> => {
        setIsSubmitting(true);
        try {
            if (!registration) throw new Error("Registration ID not found");

            await toast.promise(
                axiosClient.post(`/payments/registrations/${registration.registration_id}`, {
                    // Kirim ID gateway: "8" untuk IDR, "9" untuk USD
                    gateway_transaction_id: selectedMethod
                }),
                {
                    loading: "Menyiapkan Virtual Account...",
                    success: "Tagihan berhasil dibuat!",
                    error: (err: AxiosError<LaravelErrorResponse>) =>
                        err.response?.data?.message || "Gagal membuat tagihan.",
                }
            );

            // Invalidate Cache agar StepGuard mengizinkan pindah ke Step 4
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: ["registration-step"] }),
                queryClient.invalidateQueries({ queryKey: ["registration-status"] }),
                queryClient.invalidateQueries({ queryKey: ["payment-check"] })
            ]);

            onNext();
        } catch (err: unknown) {
            console.error(err);
            throw err;
        } finally {
            setIsSubmitting(false);
        }
    };

    // 2. Fungsi Interceptor / Pemicu Modal
    const handlePromptConfirm = (): Promise<void> => {
        // Jika sudah ada tagihan, langsung navigasi (abaikan popup)
        if (backDisabled) {
            onNext();
            return Promise.resolve();
        }

        if (!selectedMethod || !registration) {
            toast.error("Pilih metode pembayaran terlebih dahulu.");
            return Promise.reject(new Error("No method selected or registration missing"));
        }

        // Tampilkan modal popup
        setShowConfirmModal(true);

        // Tahan progress bar di tombol StepNavigation
        return new Promise((resolve, reject) => {
            promiseRef.current = { resolve, reject };
        });
    };

    // 3. Aksi Setuju di Modal
    const onConfirmModal = async () => {
        setShowConfirmModal(false);
        try {
            await executePayment();
            if (promiseRef.current) promiseRef.current.resolve();
        } catch (error: unknown) {
            if (promiseRef.current) promiseRef.current.reject(error);
        }
    };

    // 4. Aksi Batal di Modal
    const onCancelModal = () => {
        setShowConfirmModal(false);
        if (promiseRef.current) promiseRef.current.reject(new Error("User cancelled"));
    };

    if (loading) return <RegularPaymentSkeleton />;

    return (
        <div className="space-y-10 pb-32 relative">

            {/* 1. SELEKSI METODE PEMBAYARAN */}
            <section className="space-y-6">
                <div className="flex items-center gap-4">
                    <div className="bg-blue-600 p-2 rounded-lg shadow-lg shadow-blue-200 dark:shadow-none">
                        <CreditCard className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="text-lg font-black tracking-tight text-slate-900 dark:text-white uppercase">
                        Pilih Metode Pembayaran <span className="text-blue-600">Bank</span>
                    </h3>
                    <div className="h-[1px] flex-1 bg-gradient-to-r from-slate-200 to-transparent dark:from-slate-800" />
                </div>

                <div className="grid gap-5 md:grid-cols-0">
                    <PaymentMethodCard
                        id="8"
                        name="BNI Virtual Account (IDR)"
                        description="Pembayaran Rupiah via Transfer BNI"
                        logoUrl="/payment/bni.png"
                        badgeText="IDR"
                        badgeColor="orange"
                        selected={selectedMethod === "8"}
                        onSelect={setSelectedMethod}
                        disabled={backDisabled}
                    />
                    {/* <PaymentMethodCard
                        id="9"
                        name="BNI Virtual Account (USD)"
                        description="International Payment (USD Currency)"
                        logoUrl="/payment/bni.png"
                        badgeText="USD"
                        badgeColor="green"
                        selected={selectedMethod === "9"}
                        onSelect={setSelectedMethod}
                        disabled={backDisabled}
                    /> */}
                </div>
            </section>

            {/* 2. RINGKASAN PEMBAYARAN */}
            <div className="zoom-in-95">
                <PaymentSummary
                    // Nominal: Prioritaskan dari payment API, jika belum ada pakai harga paket
                    total={Number(payment?.amount || registration?.package?.price || 0)}

                    // Status & Metode
                    status={payment?.status || "unpaid"}
                    method={selectedMethod === "9" ? "BNI Virtual Account (USD)" : "BNI Virtual Account (IDR)"}
                    isUsd={selectedMethod === "9"}

                    // Informasi Paket Dinamis dari API
                    packageName={payment?.description || registration?.package?.name || "Memuat Detail Paket..."}

                    // Data Identitas Transaksi Dinamis
                    transactionId={payment?.invoice_number || "Draft Tagihan"}

                    // Tanggal Transaksi Dinamis
                    date={payment?.created_at
                        ? new Date(payment.created_at).toLocaleDateString('id-ID', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                        })
                        : "-"
                    }

                    expiryDate={payment?.expired_at_payment
                        ? payment.expired_at_payment
                        : "-"
                    }
                />
            </div>

            {/* 3. TOMBOL AKSI UTAMA (CTA) */}
            <div className="pt-4 space-y-3">
                {backDisabled ? (
                    <Button
                        onClick={() => onNext()}
                        className="w-full h-14 text-sm font-black uppercase tracking-widest bg-blue-600 hover:bg-blue-700 text-white shadow-xl shadow-blue-200 dark:shadow-none transition-all active:scale-[0.98]"
                    >
                        Lihat Instruksi Pembayaran
                        <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                ) : (
                    <Button
                        onClick={handlePromptConfirm}
                        disabled={isSubmitting || !selectedMethod}
                        className="w-full h-14 text-sm font-black uppercase tracking-widest bg-blue-600 hover:bg-blue-700 text-white shadow-xl shadow-blue-200 dark:shadow-none transition-all active:scale-[0.98] group"
                    >
                        {isSubmitting ? (
                            <Loader2 className="mr-2 h-6 w-6 animate-spin" />
                        ) : (
                            <Zap className="mr-2 h-6 w-6 fill-current group-hover:text-yellow-300 transition-colors" />
                        )}
                        Buat Tagihan Sekarang
                    </Button>
                )}
                <p className="text-center text-[10px] text-slate-400 font-medium italic uppercase tracking-widest">
                    {backDisabled
                        ? "*Tagihan Anda sudah aktif, silakan lanjutkan pembayaran."
                        : "*Klik tombol di atas untuk menerbitkan Nomor Virtual Account."}
                </p>
            </div>

            <StepNavigation
                onBack={onBack}
                onNext={handlePromptConfirm}
                nextDisabled={!selectedMethod || isSubmitting}
                backDisabled={backDisabled}
            />

            {/* MODAL KONFIRMASI (Lebih Cantik & Modern) */}
            {showConfirmModal && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-in fade-in duration-300">
                    <Card className="w-full max-w-[520px] border-0 shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300 bg-white dark:bg-slate-900 relative">

                        {/* Dekorasi Glow di Background Modal */}
                        <div className="absolute -top-24 -right-24 w-48 h-48 bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />
                        <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />

                        <div className="relative p-6 sm:p-8 space-y-6">

                            {/* Ikon Tengah (Lebih Menonjol) */}
                            <div className="flex justify-center">
                                <div className="bg-amber-100 dark:bg-amber-900/30 p-4 rounded-full ring-8 ring-amber-50 dark:ring-amber-900/10">
                                    <AlertTriangle className="w-8 h-8 text-amber-600 dark:text-amber-500" />
                                </div>
                            </div>

                            {/* Teks Judul & Deskripsi */}
                            <div className="text-center space-y-2">
                                <h3 className="text-xl font-black tracking-tight text-slate-900 dark:text-white">
                                    Konfirmasi Tagihan
                                </h3>
                                <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed px-2">
                                    Pastikan data berikut benar sebelum nomor Virtual Account diterbitkan.
                                </p>
                            </div>

                            {/* Ringkasan Detail Tagihan */}
                            <div className="grid gap-4 sm:grid-cols-2">
                                <div className="rounded-3xl border border-slate-200 dark:border-slate-700 p-4 bg-slate-50 dark:bg-slate-950">
                                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Total Tagihan</p>
                                    <p className="mt-3 text-lg font-black text-slate-900 dark:text-white">
                                        {new Intl.NumberFormat(selectedMethod === "9" ? "en-US" : "id-ID", {
                                            style: "currency",
                                            currency: selectedMethod === "9" ? "USD" : "IDR",
                                            minimumFractionDigits: selectedMethod === "9" ? 2 : 0,
                                        }).format(Number(payment?.amount || registration?.package?.price || 0))}
                                    </p>
                                </div>
                                <div className="rounded-3xl border border-slate-200 dark:border-slate-700 p-4 bg-slate-50 dark:bg-slate-950">
                                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Paket</p>
                                    <p className="mt-3 text-sm font-black text-slate-900 dark:text-white line-clamp-2">
                                        {payment?.description || registration?.package?.name || "Memuat..."}
                                    </p>
                                </div>
                                <div className="rounded-3xl border border-slate-200 dark:border-slate-700 p-4 bg-slate-50 dark:bg-slate-950 sm:col-span-2">
                                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Metode Pembayaran</p>
                                    <p className="mt-3 text-sm font-black text-slate-900 dark:text-white">
                                        {selectedMethod === "9" ? "BNI Virtual Account (USD)" : "BNI Virtual Account (IDR)"}
                                    </p>
                                </div>
                            </div>

                            {/* Kotak Info Warning (Desain Baru) */}
                            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200/70 dark:border-amber-800/50 rounded-3xl p-4 flex items-start gap-3 shadow-sm">
                                <Info className="w-5 h-5 text-amber-600 dark:text-amber-500 shrink-0 mt-0.5" />
                                <div className="text-sm font-semibold text-amber-800 dark:text-amber-200 leading-relaxed">
                                    Setelah VA diterbitkan, metode pembayaran tidak dapat diubah lagi.
                                </div>
                            </div>

                            {/* Tombol Aksi (Grid 50:50 dengan Tinggi Ekstra) */}
                            <div className="grid grid-cols-2 gap-3 pt-2">
                                <Button
                                    variant="outline"
                                    className="w-full h-12 font-bold border-slate-200 text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 transition-all"
                                    onClick={onCancelModal}
                                    disabled={isSubmitting}
                                >
                                    Batal
                                </Button>
                                <Button
                                    className="w-full h-12 font-bold bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-lg shadow-blue-500/30 transition-all active:scale-[0.98]"
                                    onClick={onConfirmModal}
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
                                    {isSubmitting ? "Memproses..." : "Ya, Buat Tagihan"}
                                </Button>
                            </div>

                        </div>
                    </Card>
                </div>
            )}
        </div>
    );
};

const RegularPaymentSkeleton = () => (
    <div className="space-y-10 pb-32">
        <div className="space-y-4">
            <Skeleton className="h-4 w-48" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Skeleton className="h-32 rounded-2xl" />
                <Skeleton className="h-32 rounded-2xl" />
            </div>
        </div>
        <Skeleton className="h-64 rounded-3xl" />
        <div className="space-y-3">
            <Skeleton className="h-14 w-full rounded-xl" />
            <Skeleton className="h-4 w-32 mx-auto" />
        </div>
        <Skeleton className="h-20 w-full rounded-2xl" />
    </div>
);